import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Swords, 
  Trophy, 
  Users, 
  Copy, 
  Check, 
  Clock,
  Crown,
  UserPlus,
  Timer
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FRONTEND_URL = window.location.origin;

export default function CompetitionsPage() {
  const [staff, setStaff] = useState([]);
  const [tests, setTests] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [groupCompetitions, setGroupCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Create 1v1 competition form
  const [showCreate, setShowCreate] = useState(false);
  const [competitionName, setCompetitionName] = useState("");
  const [selectedTest, setSelectedTest] = useState("");
  const [player1, setPlayer1] = useState("");
  const [player2, setPlayer2] = useState("");
  const [creating, setCreating] = useState(false);
  
  // Create group competition form
  const [showGroupCreate, setShowGroupCreate] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groupTest, setGroupTest] = useState("");
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [creatingGroup, setCreatingGroup] = useState(false);
  
  // View competition
  const [viewCompetition, setViewCompetition] = useState(null);
  const [viewGroupCompetition, setViewGroupCompetition] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    Promise.all([fetchStaff(), fetchTests(), fetchCompetitions(), fetchGroupCompetitions()])
      .finally(() => setLoading(false));
  }, []);

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API}/staff`);
      setStaff(response.data);
    } catch (error) {
      console.error("Failed to fetch staff:", error);
    }
  };

  const fetchTests = async () => {
    try {
      const response = await axios.get(`${API}/saved-tests`);
      setTests(response.data);
    } catch (error) {
      console.error("Failed to fetch tests:", error);
    }
  };

  const fetchCompetitions = async () => {
    try {
      const response = await axios.get(`${API}/competitions`);
      setCompetitions(response.data);
    } catch (error) {
      console.error("Failed to fetch competitions:", error);
    }
  };

  const fetchGroupCompetitions = async () => {
    try {
      const response = await axios.get(`${API}/group-competitions`);
      setGroupCompetitions(response.data);
    } catch (error) {
      console.error("Failed to fetch group competitions:", error);
    }
  };

  // 1v1 Competition handlers
  const handleCreateCompetition = async () => {
    if (!competitionName.trim()) {
      toast.error("Please enter a competition name");
      return;
    }
    if (!selectedTest) {
      toast.error("Please select a test");
      return;
    }
    if (!player1 || !player2) {
      toast.error("Please select both players");
      return;
    }
    if (player1 === player2) {
      toast.error("Please select different players");
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/competitions`, {
        name: competitionName.trim(),
        test_id: selectedTest,
        player1_id: player1,
        player2_id: player2
      });
      setCompetitions(prev => [response.data, ...prev]);
      setShowCreate(false);
      setViewCompetition(response.data);
      resetForm();
      toast.success("Competition created!");
    } catch (error) {
      toast.error("Failed to create competition");
    } finally {
      setCreating(false);
    }
  };

  // Group Competition handlers
  const handleCreateGroupCompetition = async () => {
    if (!groupName.trim()) {
      toast.error("Please enter a competition name");
      return;
    }
    if (!groupTest) {
      toast.error("Please select a test");
      return;
    }
    if (selectedPlayers.length < 2) {
      toast.error("Please select at least 2 players");
      return;
    }
    if (selectedPlayers.length > 12) {
      toast.error("Maximum 12 players allowed");
      return;
    }

    setCreatingGroup(true);
    try {
      const response = await axios.post(`${API}/group-competitions`, {
        name: groupName.trim(),
        test_id: groupTest,
        player_ids: selectedPlayers
      });
      setGroupCompetitions(prev => [response.data, ...prev]);
      setShowGroupCreate(false);
      setViewGroupCompetition(response.data);
      resetGroupForm();
      toast.success("Group competition created!");
    } catch (error) {
      toast.error("Failed to create group competition");
    } finally {
      setCreatingGroup(false);
    }
  };

  const togglePlayerSelection = (playerId) => {
    setSelectedPlayers(prev => 
      prev.includes(playerId) 
        ? prev.filter(id => id !== playerId)
        : prev.length < 12 ? [...prev, playerId] : prev
    );
  };

  const resetForm = () => {
    setCompetitionName("");
    setSelectedTest("");
    setPlayer1("");
    setPlayer2("");
  };

  const resetGroupForm = () => {
    setGroupName("");
    setGroupTest("");
    setSelectedPlayers([]);
  };

  const copyLink = (code, type = "compete") => {
    const url = `${FRONTEND_URL}/${type}/${code}`;
    navigator.clipboard.writeText(url);
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
    toast.success("Link copied!");
  };

  const generateQRCode = (code, type = "compete") => {
    return `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(`${FRONTEND_URL}/${type}/${code}`)}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return <span className="px-2 py-1 text-xs rounded bg-green-500/20 text-green-400">Completed</span>;
      case "in_progress":
        return <span className="px-2 py-1 text-xs rounded bg-[#C36A2F]/20 text-[#C36A2F]">In Progress</span>;
      case "expired":
        return <span className="px-2 py-1 text-xs rounded bg-red-500/20 text-red-400">Expired</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded bg-blue-500/20 text-blue-400">Pending</span>;
    }
  };

  const formatDeadline = (deadline) => {
    const d = new Date(deadline);
    const now = new Date();
    const diff = d - now;
    
    if (diff < 0) return "Expired";
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) return `${hours}h ${minutes}m left`;
    return `${minutes}m left`;
  };

  return (
    <div data-testid="competitions-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3">
            <Swords className="text-red-500" size={36} />
            Competitions
          </h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-2">
            Create 1v1 battles or group competitions
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowCreate(true)}
            variant="outline"
            data-testid="create-1v1-btn"
          >
            <Swords size={18} className="mr-2" />
            1v1 Battle
          </Button>
          <Button
            onClick={() => setShowGroupCreate(true)}
            className="btn-glow bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90"
            data-testid="create-group-btn"
          >
            <Users size={18} className="mr-2" />
            Group Competition
          </Button>
        </div>
      </div>

      <Tabs defaultValue="group" className="w-full">
        <TabsList className="bg-[hsl(var(--secondary))]">
          <TabsTrigger value="group" data-testid="group-tab">
            <Users size={16} className="mr-2" />
            Group ({groupCompetitions.length})
          </TabsTrigger>
          <TabsTrigger value="1v1" data-testid="1v1-tab">
            <Swords size={16} className="mr-2" />
            1v1 ({competitions.length})
          </TabsTrigger>
        </TabsList>

        {/* Group Competitions Tab */}
        <TabsContent value="group" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Active Group Competitions */}
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="text-[#C36A2F]" size={20} />
                  Active Competitions
                </CardTitle>
              </CardHeader>
              <CardContent>
                {groupCompetitions.filter(c => !['completed', 'expired'].includes(c.status)).length === 0 ? (
                  <p className="text-center py-6 text-[hsl(var(--muted-foreground))]">
                    No active group competitions
                  </p>
                ) : (
                  <div className="space-y-3">
                    {groupCompetitions.filter(c => !['completed', 'expired'].includes(c.status)).map((comp) => {
                      const completedCount = comp.players?.filter(p => p.result_id).length || 0;
                      const totalPlayers = comp.players?.length || 0;
                      
                      return (
                        <div
                          key={comp.id}
                          className="p-4 rounded-lg bg-[hsl(var(--secondary))] cursor-pointer hover:bg-[hsl(var(--accent))] transition-colors"
                          onClick={() => setViewGroupCompetition(comp)}
                          data-testid={`group-competition-${comp.id}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-semibold">{comp.name}</p>
                            {getStatusBadge(comp.status)}
                          </div>
                          <div className="flex items-center justify-between text-sm text-[hsl(var(--muted-foreground))]">
                            <span className="flex items-center gap-1">
                              <Users size={14} />
                              {completedCount}/{totalPlayers} completed
                            </span>
                            <span className="flex items-center gap-1">
                              <Timer size={14} />
                              {formatDeadline(comp.deadline)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Completed Group Competitions */}
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="text-[hsl(var(--primary))]" size={20} />
                  Results
                </CardTitle>
              </CardHeader>
              <CardContent>
                {groupCompetitions.filter(c => ['completed', 'expired'].includes(c.status)).length === 0 ? (
                  <p className="text-center py-6 text-[hsl(var(--muted-foreground))]">
                    No completed competitions yet
                  </p>
                ) : (
                  <div className="space-y-3">
                    {groupCompetitions.filter(c => ['completed', 'expired'].includes(c.status)).slice(0, 5).map((comp) => {
                      const winner = comp.rankings?.[0];
                      
                      return (
                        <div
                          key={comp.id}
                          className="p-4 rounded-lg bg-[hsl(var(--secondary))] cursor-pointer hover:bg-[hsl(var(--accent))] transition-colors"
                          onClick={() => setViewGroupCompetition(comp)}
                          data-testid={`completed-group-${comp.id}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-semibold">{comp.name}</p>
                            {getStatusBadge(comp.status)}
                          </div>
                          {winner && (
                            <div className="flex items-center gap-2">
                              <Crown size={16} className="text-[hsl(var(--primary))]" />
                              <span className="text-[hsl(var(--primary))] font-medium">
                                {winner.staff_name} ({winner.percentage?.toFixed(0)}%)
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* 1v1 Competitions Tab */}
        <TabsContent value="1v1" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Active 1v1 Competitions */}
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="text-[#C36A2F]" size={20} />
                  Active Battles
                </CardTitle>
              </CardHeader>
              <CardContent>
                {competitions.filter(c => c.status !== 'completed').length === 0 ? (
                  <p className="text-center py-6 text-[hsl(var(--muted-foreground))]">
                    No active 1v1 battles
                  </p>
                ) : (
                  <div className="space-y-3">
                    {competitions.filter(c => c.status !== 'completed').map((comp) => (
                      <div
                        key={comp.id}
                        className="p-4 rounded-lg bg-[hsl(var(--secondary))] cursor-pointer hover:bg-[hsl(var(--accent))] transition-colors"
                        onClick={() => setViewCompetition(comp)}
                        data-testid={`competition-${comp.id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-semibold">{comp.name}</p>
                          {getStatusBadge(comp.status)}
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <span className={comp.player1_score !== null ? 'text-green-400' : 'text-[hsl(var(--muted-foreground))]'}>
                            {comp.player1_name}
                            {comp.player1_score !== null && ` (${comp.player1_score.toFixed(0)}%)`}
                          </span>
                          <Swords size={14} className="text-[hsl(var(--muted-foreground))]" />
                          <span className={comp.player2_score !== null ? 'text-green-400' : 'text-[hsl(var(--muted-foreground))]'}>
                            {comp.player2_name}
                            {comp.player2_score !== null && ` (${comp.player2_score.toFixed(0)}%)`}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Completed 1v1 Competitions */}
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="text-[hsl(var(--primary))]" size={20} />
                  Recent Results
                </CardTitle>
              </CardHeader>
              <CardContent>
                {competitions.filter(c => c.status === 'completed').length === 0 ? (
                  <p className="text-center py-6 text-[hsl(var(--muted-foreground))]">
                    No completed battles yet
                  </p>
                ) : (
                  <div className="space-y-3">
                    {competitions.filter(c => c.status === 'completed').slice(0, 5).map((comp) => {
                      const isTie = comp.winner_id === 'tie';
                      const winner = isTie ? null : (comp.winner_id === comp.player1_id ? comp.player1_name : comp.player2_name);
                      
                      return (
                        <div
                          key={comp.id}
                          className="p-4 rounded-lg bg-[hsl(var(--secondary))]"
                          data-testid={`completed-${comp.id}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-semibold">{comp.name}</p>
                            {isTie ? (
                              <span className="text-[#C36A2F] text-sm font-medium">TIE!</span>
                            ) : (
                              <span className="flex items-center gap-1 text-[hsl(var(--primary))]">
                                <Crown size={14} />
                                <span className="text-sm font-medium">{winner}</span>
                              </span>
                            )}
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className={comp.winner_id === comp.player1_id ? 'text-green-400 font-medium' : 'text-[hsl(var(--muted-foreground))]'}>
                              {comp.player1_name}: {comp.player1_score?.toFixed(0)}%
                            </span>
                            <span className={comp.winner_id === comp.player2_id ? 'text-green-400 font-medium' : 'text-[hsl(var(--muted-foreground))]'}>
                              {comp.player2_name}: {comp.player2_score?.toFixed(0)}%
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create 1v1 Competition Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md" data-testid="create-competition-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Swords className="text-red-500" size={24} />
              Create 1v1 Battle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Competition Name</Label>
              <Input
                value={competitionName}
                onChange={(e) => setCompetitionName(e.target.value)}
                placeholder="e.g., Friday Night Showdown"
                className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]"
                data-testid="competition-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Select Test</Label>
              <Select value={selectedTest} onValueChange={setSelectedTest}>
                <SelectTrigger data-testid="select-test">
                  <SelectValue placeholder="Choose a test..." />
                </SelectTrigger>
                <SelectContent>
                  {tests.map((test) => (
                    <SelectItem key={test.id} value={test.id}>
                      {test.name} ({test.cocktail_ids.length} cocktails)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Player 1</Label>
                <Select value={player1} onValueChange={setPlayer1}>
                  <SelectTrigger data-testid="select-player1">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {staff.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Player 2</Label>
                <Select value={player2} onValueChange={setPlayer2}>
                  <SelectTrigger data-testid="select-player2">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {staff.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button
              onClick={handleCreateCompetition}
              disabled={creating}
              className="w-full btn-glow bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90"
              data-testid="confirm-create-btn"
            >
              {creating ? "Creating..." : "Create Battle"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Group Competition Dialog */}
      <Dialog open={showGroupCreate} onOpenChange={setShowGroupCreate}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md" data-testid="create-group-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="text-[hsl(var(--primary))]" size={24} />
              Create Group Competition
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <p className="text-sm text-blue-400">
                <Timer size={14} className="inline mr-1" />
                Players have 24 hours to complete. Results shown after everyone finishes or deadline passes.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Competition Name</Label>
              <Input
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="e.g., Weekend Championship"
                className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]"
                data-testid="group-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Select Test</Label>
              <Select value={groupTest} onValueChange={setGroupTest}>
                <SelectTrigger data-testid="select-group-test">
                  <SelectValue placeholder="Choose a test..." />
                </SelectTrigger>
                <SelectContent>
                  {tests.map((test) => (
                    <SelectItem key={test.id} value={test.id}>
                      {test.name} ({test.cocktail_ids.length} cocktails)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Select Players (2-12)</Label>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                {selectedPlayers.length} selected
              </p>
              <div className="max-h-48 overflow-y-auto space-y-2 p-2 rounded-lg bg-[hsl(var(--secondary))]">
                {staff.map((s) => (
                  <div
                    key={s.id}
                    onClick={() => togglePlayerSelection(s.id)}
                    className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                      selectedPlayers.includes(s.id)
                        ? 'bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/30'
                        : 'hover:bg-[hsl(var(--accent))]'
                    }`}
                    data-testid={`select-player-${s.id}`}
                  >
                    <Checkbox checked={selectedPlayers.includes(s.id)} />
                    <span>{s.name}</span>
                  </div>
                ))}
              </div>
            </div>

            <Button
              onClick={handleCreateGroupCompetition}
              disabled={creatingGroup || selectedPlayers.length < 2}
              className="w-full btn-glow bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90"
              data-testid="confirm-create-group-btn"
            >
              {creatingGroup ? "Creating..." : `Create Competition (${selectedPlayers.length} players)`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View 1v1 Competition Dialog */}
      <Dialog open={!!viewCompetition} onOpenChange={() => setViewCompetition(null)}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-lg" data-testid="view-competition-dialog">
          <DialogHeader>
            <DialogTitle>{viewCompetition?.name}</DialogTitle>
          </DialogHeader>
          {viewCompetition && (
            <div className="space-y-6">
              {/* VS Display */}
              <div className="flex items-center justify-center gap-6 py-4">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-2">
                    <span className="text-2xl font-bold text-blue-400">
                      {viewCompetition.player1_name?.charAt(0)}
                    </span>
                  </div>
                  <p className="font-semibold">{viewCompetition.player1_name}</p>
                  {viewCompetition.player1_score !== null && (
                    <p className={`text-2xl font-bold ${viewCompetition.winner_id === viewCompetition.player1_id ? 'text-green-500' : ''}`}>
                      {viewCompetition.player1_score?.toFixed(0)}%
                    </p>
                  )}
                </div>
                
                <div className="text-center">
                  <Swords size={40} className="text-red-500 mx-auto" />
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">VS</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-2">
                    <span className="text-2xl font-bold text-red-400">
                      {viewCompetition.player2_name?.charAt(0)}
                    </span>
                  </div>
                  <p className="font-semibold">{viewCompetition.player2_name}</p>
                  {viewCompetition.player2_score !== null && (
                    <p className={`text-2xl font-bold ${viewCompetition.winner_id === viewCompetition.player2_id ? 'text-green-500' : ''}`}>
                      {viewCompetition.player2_score?.toFixed(0)}%
                    </p>
                  )}
                </div>
              </div>

              {viewCompetition.status === 'completed' ? (
                <div className="text-center p-4 rounded-lg bg-[hsl(var(--secondary))]">
                  {viewCompetition.winner_id === 'tie' ? (
                    <p className="text-xl font-bold text-[#C36A2F]">It's a TIE!</p>
                  ) : (
                    <>
                      <Crown className="text-[hsl(var(--primary))] mx-auto mb-2" size={32} />
                      <p className="text-xl font-bold text-[hsl(var(--primary))]">
                        {viewCompetition.winner_id === viewCompetition.player1_id 
                          ? viewCompetition.player1_name 
                          : viewCompetition.player2_name} Wins!
                      </p>
                    </>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-[hsl(var(--muted-foreground))] text-center">
                    Share these links with the players
                  </p>
                  
                  {/* Player 1 Link */}
                  <div className="p-3 rounded-lg bg-[hsl(var(--secondary))]">
                    <p className="font-medium mb-2 text-blue-400">{viewCompetition.player1_name}&apos;s Link</p>
                    <div className="flex gap-2 mb-2">
                      <Input
                        readOnly
                        value={`${FRONTEND_URL}/compete/${viewCompetition.player1_access_code}`}
                        className="bg-[hsl(var(--card))] border-[hsl(var(--border))] text-xs"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyLink(viewCompetition.player1_access_code)}
                      >
                        {copied === viewCompetition.player1_access_code ? <Check size={14} /> : <Copy size={14} />}
                      </Button>
                    </div>
                    <div className="flex justify-center">
                      <img src={generateQRCode(viewCompetition.player1_access_code)} alt="QR" className="w-20 h-20 rounded" />
                    </div>
                  </div>
                  
                  {/* Player 2 Link */}
                  <div className="p-3 rounded-lg bg-[hsl(var(--secondary))]">
                    <p className="font-medium mb-2 text-red-400">{viewCompetition.player2_name}&apos;s Link</p>
                    <div className="flex gap-2 mb-2">
                      <Input
                        readOnly
                        value={`${FRONTEND_URL}/compete/${viewCompetition.player2_access_code}`}
                        className="bg-[hsl(var(--card))] border-[hsl(var(--border))] text-xs"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyLink(viewCompetition.player2_access_code)}
                      >
                        {copied === viewCompetition.player2_access_code ? <Check size={14} /> : <Copy size={14} />}
                      </Button>
                    </div>
                    <div className="flex justify-center">
                      <img src={generateQRCode(viewCompetition.player2_access_code)} alt="QR" className="w-20 h-20 rounded" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Group Competition Dialog */}
      <Dialog open={!!viewGroupCompetition} onOpenChange={() => setViewGroupCompetition(null)}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="view-group-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="text-[hsl(var(--primary))]" size={24} />
              {viewGroupCompetition?.name}
            </DialogTitle>
          </DialogHeader>
          {viewGroupCompetition && (
            <div className="space-y-6">
              {/* Status and deadline */}
              <div className="flex items-center justify-between">
                {getStatusBadge(viewGroupCompetition.status)}
                {!['completed', 'expired'].includes(viewGroupCompetition.status) && (
                  <span className="text-sm text-[hsl(var(--muted-foreground))] flex items-center gap-1">
                    <Timer size={14} />
                    {formatDeadline(viewGroupCompetition.deadline)}
                  </span>
                )}
              </div>

              {/* Rankings (if completed/expired) */}
              {['completed', 'expired'].includes(viewGroupCompetition.status) && viewGroupCompetition.rankings ? (
                <div className="space-y-3">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Trophy className="text-[hsl(var(--primary))]" size={18} />
                    Final Rankings
                  </h3>
                  {viewGroupCompetition.rankings.map((player, idx) => (
                    <div
                      key={player.staff_id}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        idx === 0 ? 'bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/30' : 'bg-[hsl(var(--secondary))]'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                          idx === 0 ? 'bg-[hsl(var(--primary))] text-black' :
                          idx === 1 ? 'bg-gray-400 text-black' :
                          idx === 2 ? 'bg-amber-600 text-black' :
                          'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]'
                        }`}>
                          {idx + 1}
                        </span>
                        <span className={`font-medium ${idx === 0 ? 'text-[hsl(var(--primary))]' : ''}`}>
                          {player.staff_name}
                        </span>
                        {idx === 0 && <Crown size={16} className="text-[hsl(var(--primary))]" />}
                      </div>
                      <span className={`font-bold ${
                        player.completed ? (
                          player.percentage >= 80 ? 'text-green-400' :
                          player.percentage >= 60 ? 'text-[#C36A2F]' : 'text-red-400'
                        ) : 'text-[hsl(var(--muted-foreground))]'
                      }`}>
                        {player.completed ? `${player.percentage?.toFixed(0)}%` : 'DNF'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                /* Player links (if active) */
                <div className="space-y-3">
                  <h3 className="font-semibold">Share links with players:</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {viewGroupCompetition.players?.map((player, idx) => (
                      <div
                        key={player.staff_id}
                        className={`p-3 rounded-lg ${
                          player.result_id ? 'bg-green-500/10 border border-green-500/30' : 'bg-[hsl(var(--secondary))]'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">{player.staff_name}</span>
                          {player.result_id ? (
                            <span className="text-xs px-2 py-1 rounded bg-green-500/20 text-green-400">
                              Completed {player.percentage?.toFixed(0)}%
                            </span>
                          ) : (
                            <span className="text-xs px-2 py-1 rounded bg-[#C36A2F]/20 text-[#C36A2F]">
                              Pending
                            </span>
                          )}
                        </div>
                        {!player.result_id && (
                          <div className="flex gap-2">
                            <Input
                              readOnly
                              value={`${FRONTEND_URL}/group-compete/${player.access_code}`}
                              className="bg-[hsl(var(--card))] border-[hsl(var(--border))] text-xs"
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => copyLink(player.access_code, "group-compete")}
                            >
                              {copied === player.access_code ? <Check size={14} /> : <Copy size={14} />}
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
